/**
 * Loans Domain Seed Data
 *
 * Contains realistic loan application data with:
 * - Bridge/Fix & Flip loans
 * - DSCR Rental loans
 * - Construction loans
 * - Commercial loans
 *
 * Each loan includes realistic:
 * - Property addresses (real US locations)
 * - Loan amounts, rates, terms
 * - LTV/LTC ratios
 * - Borrower credit scores
 *
 * @see 32-entity-framework-service for loan entity storage
 */

import { seedId } from '../utils';
import { CONSUMER_IDS } from './consumers';
import { BUSINESS_IDS, REALM_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// LOAN IDs
// =============================================================================

export const LOAN_IDS = {
  // Bridge/Fix & Flip Loans
  BRIDGE_MARCUS_AUSTIN_RETAIL: seedId('loan-marcus-austin', 1),
  BRIDGE_MARCUS_DALLAS_OFFICE: seedId('loan-marcus-dallas', 2),
  BRIDGE_JESSICA_HOUSTON_SFR: seedId('loan-jessica-houston', 1),
  BRIDGE_JENNIFER_AUSTIN_FLIP: seedId('loan-jennifer-austin', 1),

  // DSCR Rental Loans
  DSCR_ROBERT_HOUSTON_QUADPLEX: seedId('loan-robert-quad', 1),
  DSCR_ALICIA_TAMPA_4PLEX: seedId('loan-alicia-tampa', 1),
  DSCR_DAVID_DALLAS_8UNIT: seedId('loan-david-dallas', 1),
  DSCR_MICHAEL_HOUSTON_TRIPLEX: seedId('loan-michael-triplex', 1),

  // Construction Loans
  CONSTRUCTION_MARIA_SA_MULTIFAMILY: seedId('loan-maria-construction', 1),
  CONSTRUCTION_CHRIS_FORTWORTH_SFR: seedId('loan-chris-construction', 1),

  // Commercial Loans
  COMMERCIAL_ROBERT_HOUSTON_RETAIL: seedId('loan-robert-commercial', 1),
  COMMERCIAL_MARCUS_AUSTIN_OFFICE: seedId('loan-marcus-commercial', 1),
} as const;

export type LoanId = (typeof LOAN_IDS)[keyof typeof LOAN_IDS];

// =============================================================================
// PROPERTY IDs
// =============================================================================

export const PROPERTY_IDS = {
  // Austin Properties
  AUSTIN_2ND_STREET_RETAIL: seedId('prop-austin-2nd', 1),
  AUSTIN_CONGRESS_OFFICE: seedId('prop-austin-congress', 2),
  AUSTIN_MANOR_RD_SFR: seedId('prop-austin-manor', 3),
  AUSTIN_E_7TH_FLIP: seedId('prop-austin-7th', 4),

  // Dallas/Fort Worth Properties
  DALLAS_MAIN_OFFICE: seedId('prop-dallas-main', 1),
  DALLAS_ELM_8UNIT: seedId('prop-dallas-elm', 2),
  FORTWORTH_CAMP_BOWIE: seedId('prop-fortworth-camp', 1),

  // Houston Properties
  HOUSTON_WESTHEIMER_QUADPLEX: seedId('prop-houston-westheimer', 1),
  HOUSTON_MONTROSE_TRIPLEX: seedId('prop-houston-montrose', 2),
  HOUSTON_KIRBY_RETAIL: seedId('prop-houston-kirby', 3),
  HOUSTON_HEIGHTS_SFR: seedId('prop-houston-heights', 4),

  // Tampa/Florida Properties
  TAMPA_BAYSHORE_4PLEX: seedId('prop-tampa-bayshore', 1),

  // San Antonio Properties
  SA_BROADWAY_MULTIFAMILY: seedId('prop-sa-broadway', 1),
} as const;

export type PropertyId = (typeof PROPERTY_IDS)[keyof typeof PROPERTY_IDS];

// =============================================================================
// LOAN STATUS TYPES
// =============================================================================

export const LOAN_STATUSES = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  IN_REVIEW: 'in_review',
  UNDERWRITING: 'underwriting',
  CONDITIONAL_APPROVAL: 'conditional_approval',
  APPROVED: 'approved',
  CLEAR_TO_CLOSE: 'clear_to_close',
  FUNDED: 'funded',
  ACTIVE: 'active',
  PAID_OFF: 'paid_off',
  DENIED: 'denied',
  WITHDRAWN: 'withdrawn',
} as const;

export type LoanStatus = (typeof LOAN_STATUSES)[keyof typeof LOAN_STATUSES];

// =============================================================================
// LOAN PRODUCT TYPES
// =============================================================================

export const SEED_LOAN_PRODUCTS = {
  BRIDGE: 'bridge',
  FIX_FLIP: 'fix_flip',
  DSCR: 'dscr',
  CONSTRUCTION: 'construction',
  GROUND_UP: 'ground_up',
  COMMERCIAL: 'commercial',
  HARD_MONEY: 'hard_money',
} as const;

export type SeedLoanProduct = (typeof SEED_LOAN_PRODUCTS)[keyof typeof SEED_LOAN_PRODUCTS];

// =============================================================================
// PROPERTY TYPES
// =============================================================================

export const SEED_PROPERTY_TYPES = {
  SFR: 'sfr',
  CONDO: 'condo',
  TOWNHOUSE: 'townhouse',
  DUPLEX: 'duplex',
  TRIPLEX: 'triplex',
  QUADPLEX: 'quadplex',
  MULTI_FAMILY_5_PLUS: 'multi_family_5_plus',
  MIXED_USE: 'mixed_use',
  RETAIL: 'retail',
  OFFICE: 'office',
  INDUSTRIAL: 'industrial',
  LAND: 'land',
} as const;

export type SeedPropertyType = (typeof SEED_PROPERTY_TYPES)[keyof typeof SEED_PROPERTY_TYPES];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface PropertyAddressSeedData {
  street: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
}

export interface PropertySeedData {
  id: string;
  businessId: string;
  address: PropertyAddressSeedData;
  propertyType: SeedPropertyType;
  yearBuilt?: number;
  squareFeet?: number;
  lotSize?: number; // in sqft
  bedrooms?: number;
  bathrooms?: number;
  units?: number;
  purchasePrice?: number;
  asIsValue?: number;
  afterRepairValue?: number;
  monthlyRent?: number;
  monthlyNOI?: number;
  occupancyRate?: number;
  condition?: 'excellent' | 'good' | 'fair' | 'poor';
  appraisalDate?: string;
  appraisalValue?: number;
}

export interface LoanSeedData {
  id: string;
  businessId: string;
  realmId: string;
  loanNumber: string;
  consumerId: string;
  propertyId: string;

  // Loan Product Details
  loanProduct: SeedLoanProduct;
  loanPurpose: 'purchase' | 'refinance' | 'cash_out' | 'construction';

  // Financial Terms
  loanAmount: number;
  purchasePrice?: number;
  estimatedValue: number;
  ltvRatio: number;
  ltcRatio?: number; // For construction
  interestRate: number;
  originationFee: number; // as percentage (e.g., 2.0 = 2%)
  termMonths: number;
  interestOnlyPeriod?: number;

  // Borrower Qualification
  creditScore: number;
  liquidAssets: number;
  experienceLevel: 'first_time' | 'experienced' | 'professional';
  completedDeals: number;

  // DSCR-specific
  dscrRatio?: number;
  monthlyRent?: number;
  monthlyDebtService?: number;

  // Construction-specific
  constructionBudget?: number;
  hardCosts?: number;
  softCosts?: number;
  contingency?: number;
  drawsRemaining?: number;

  // Status
  status: LoanStatus;
  applicationDate: string;
  approvalDate?: string;
  fundingDate?: string;
  maturityDate?: string;

  // Assignment
  loanOfficerId?: string;
  processorId?: string;
  underwriterId?: string;

  // Notes
  notes?: string;
}

// =============================================================================
// PROPERTY DATA - Real US Addresses
// =============================================================================

export const PROPERTIES: Record<keyof typeof PROPERTY_IDS, PropertySeedData> = {
  // Austin Properties
  AUSTIN_2ND_STREET_RETAIL: {
    id: PROPERTY_IDS.AUSTIN_2ND_STREET_RETAIL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    address: {
      street: '200 W 2nd Street',
      city: 'Austin',
      state: 'TX',
      zip: '78701',
      county: 'Travis',
    },
    propertyType: SEED_PROPERTY_TYPES.RETAIL,
    yearBuilt: 2008,
    squareFeet: 8500,
    lotSize: 12000,
    purchasePrice: 2800000,
    asIsValue: 2650000,
    afterRepairValue: 3200000,
    monthlyRent: 28500,
    monthlyNOI: 22800,
    occupancyRate: 95,
    condition: 'good',
    appraisalDate: '2026-01-10',
    appraisalValue: 2750000,
  },

  AUSTIN_CONGRESS_OFFICE: {
    id: PROPERTY_IDS.AUSTIN_CONGRESS_OFFICE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    address: {
      street: '500 Congress Avenue',
      unit: 'Suite 1200',
      city: 'Austin',
      state: 'TX',
      zip: '78701',
      county: 'Travis',
    },
    propertyType: SEED_PROPERTY_TYPES.OFFICE,
    yearBuilt: 2015,
    squareFeet: 12000,
    purchasePrice: 5500000,
    asIsValue: 5200000,
    afterRepairValue: 5800000,
    monthlyRent: 48000,
    monthlyNOI: 38500,
    occupancyRate: 100,
    condition: 'excellent',
    appraisalDate: '2026-01-05',
    appraisalValue: 5400000,
  },

  AUSTIN_MANOR_RD_SFR: {
    id: PROPERTY_IDS.AUSTIN_MANOR_RD_SFR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    address: {
      street: '3847 Manor Road',
      city: 'Austin',
      state: 'TX',
      zip: '78723',
      county: 'Travis',
    },
    propertyType: SEED_PROPERTY_TYPES.SFR,
    yearBuilt: 1962,
    squareFeet: 1850,
    lotSize: 7200,
    bedrooms: 3,
    bathrooms: 2,
    purchasePrice: 385000,
    asIsValue: 365000,
    afterRepairValue: 525000,
    condition: 'fair',
    appraisalDate: '2026-01-15',
    appraisalValue: 375000,
  },

  AUSTIN_E_7TH_FLIP: {
    id: PROPERTY_IDS.AUSTIN_E_7TH_FLIP,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    address: {
      street: '1205 E 7th Street',
      city: 'Austin',
      state: 'TX',
      zip: '78702',
      county: 'Travis',
    },
    propertyType: SEED_PROPERTY_TYPES.SFR,
    yearBuilt: 1955,
    squareFeet: 1420,
    lotSize: 5500,
    bedrooms: 2,
    bathrooms: 1,
    purchasePrice: 280000,
    asIsValue: 265000,
    afterRepairValue: 425000,
    condition: 'poor',
    appraisalDate: '2026-01-20',
    appraisalValue: 270000,
  },

  // Dallas/Fort Worth Properties
  DALLAS_MAIN_OFFICE: {
    id: PROPERTY_IDS.DALLAS_MAIN_OFFICE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    address: {
      street: '1401 Main Street',
      unit: 'Suite 800',
      city: 'Dallas',
      state: 'TX',
      zip: '75202',
      county: 'Dallas',
    },
    propertyType: SEED_PROPERTY_TYPES.OFFICE,
    yearBuilt: 1998,
    squareFeet: 15000,
    purchasePrice: 4200000,
    asIsValue: 4000000,
    afterRepairValue: 4800000,
    monthlyRent: 35000,
    monthlyNOI: 28000,
    occupancyRate: 92,
    condition: 'good',
    appraisalDate: '2026-01-08',
    appraisalValue: 4100000,
  },

  DALLAS_ELM_8UNIT: {
    id: PROPERTY_IDS.DALLAS_ELM_8UNIT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    address: {
      street: '2847 Elm Street',
      city: 'Dallas',
      state: 'TX',
      zip: '75226',
      county: 'Dallas',
    },
    propertyType: SEED_PROPERTY_TYPES.MULTI_FAMILY_5_PLUS,
    yearBuilt: 1972,
    squareFeet: 8400,
    lotSize: 12000,
    units: 8,
    purchasePrice: 1650000,
    asIsValue: 1550000,
    afterRepairValue: 2100000,
    monthlyRent: 14400,
    monthlyNOI: 11520,
    occupancyRate: 88,
    condition: 'fair',
    appraisalDate: '2026-01-12',
    appraisalValue: 1600000,
  },

  FORTWORTH_CAMP_BOWIE: {
    id: PROPERTY_IDS.FORTWORTH_CAMP_BOWIE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    address: {
      street: '4521 Camp Bowie Boulevard',
      city: 'Fort Worth',
      state: 'TX',
      zip: '76107',
      county: 'Tarrant',
    },
    propertyType: SEED_PROPERTY_TYPES.SFR,
    yearBuilt: 1948,
    squareFeet: 2200,
    lotSize: 8500,
    bedrooms: 4,
    bathrooms: 2,
    purchasePrice: 320000,
    asIsValue: 295000,
    afterRepairValue: 485000,
    condition: 'fair',
    appraisalDate: '2026-01-18',
    appraisalValue: 310000,
  },

  // Houston Properties
  HOUSTON_WESTHEIMER_QUADPLEX: {
    id: PROPERTY_IDS.HOUSTON_WESTHEIMER_QUADPLEX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    address: {
      street: '6205 Westheimer Road',
      city: 'Houston',
      state: 'TX',
      zip: '77057',
      county: 'Harris',
    },
    propertyType: SEED_PROPERTY_TYPES.QUADPLEX,
    yearBuilt: 1985,
    squareFeet: 4800,
    lotSize: 9600,
    units: 4,
    purchasePrice: 895000,
    asIsValue: 875000,
    afterRepairValue: 1050000,
    monthlyRent: 8200,
    monthlyNOI: 6560,
    occupancyRate: 100,
    condition: 'good',
    appraisalDate: '2026-01-06',
    appraisalValue: 890000,
  },

  HOUSTON_MONTROSE_TRIPLEX: {
    id: PROPERTY_IDS.HOUSTON_MONTROSE_TRIPLEX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    address: {
      street: '1823 Montrose Boulevard',
      city: 'Houston',
      state: 'TX',
      zip: '77006',
      county: 'Harris',
    },
    propertyType: SEED_PROPERTY_TYPES.TRIPLEX,
    yearBuilt: 1978,
    squareFeet: 3600,
    lotSize: 6400,
    units: 3,
    purchasePrice: 725000,
    asIsValue: 700000,
    afterRepairValue: 885000,
    monthlyRent: 6450,
    monthlyNOI: 5160,
    occupancyRate: 100,
    condition: 'good',
    appraisalDate: '2026-01-14',
    appraisalValue: 715000,
  },

  HOUSTON_KIRBY_RETAIL: {
    id: PROPERTY_IDS.HOUSTON_KIRBY_RETAIL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    address: {
      street: '3100 Kirby Drive',
      city: 'Houston',
      state: 'TX',
      zip: '77098',
      county: 'Harris',
    },
    propertyType: SEED_PROPERTY_TYPES.RETAIL,
    yearBuilt: 1992,
    squareFeet: 12500,
    lotSize: 18000,
    purchasePrice: 4800000,
    asIsValue: 4600000,
    afterRepairValue: 5200000,
    monthlyRent: 42000,
    monthlyNOI: 33600,
    occupancyRate: 100,
    condition: 'excellent',
    appraisalDate: '2026-01-03',
    appraisalValue: 4750000,
  },

  HOUSTON_HEIGHTS_SFR: {
    id: PROPERTY_IDS.HOUSTON_HEIGHTS_SFR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    address: {
      street: '415 W 18th Street',
      city: 'Houston',
      state: 'TX',
      zip: '77008',
      county: 'Harris',
    },
    propertyType: SEED_PROPERTY_TYPES.SFR,
    yearBuilt: 1925,
    squareFeet: 1680,
    lotSize: 5800,
    bedrooms: 3,
    bathrooms: 2,
    purchasePrice: 345000,
    asIsValue: 325000,
    afterRepairValue: 495000,
    condition: 'fair',
    appraisalDate: '2026-01-22',
    appraisalValue: 335000,
  },

  // Tampa Properties
  TAMPA_BAYSHORE_4PLEX: {
    id: PROPERTY_IDS.TAMPA_BAYSHORE_4PLEX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    address: {
      street: '4912 Bayshore Boulevard',
      city: 'Tampa',
      state: 'FL',
      zip: '33611',
      county: 'Hillsborough',
    },
    propertyType: SEED_PROPERTY_TYPES.QUADPLEX,
    yearBuilt: 1988,
    squareFeet: 5200,
    lotSize: 10400,
    units: 4,
    purchasePrice: 1125000,
    asIsValue: 1100000,
    afterRepairValue: 1350000,
    monthlyRent: 8800,
    monthlyNOI: 7040,
    occupancyRate: 100,
    condition: 'good',
    appraisalDate: '2026-01-09',
    appraisalValue: 1115000,
  },

  // San Antonio Properties
  SA_BROADWAY_MULTIFAMILY: {
    id: PROPERTY_IDS.SA_BROADWAY_MULTIFAMILY,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    address: {
      street: '8200 Broadway Street',
      city: 'San Antonio',
      state: 'TX',
      zip: '78209',
      county: 'Bexar',
    },
    propertyType: SEED_PROPERTY_TYPES.MULTI_FAMILY_5_PLUS,
    yearBuilt: 0, // New construction
    squareFeet: 9600,
    lotSize: 24000,
    units: 8,
    purchasePrice: 450000, // Land value
    asIsValue: 450000,
    afterRepairValue: 2400000, // Completed value
    condition: 'fair', // Vacant lot
    appraisalDate: '2026-01-16',
    appraisalValue: 460000,
  },
};

// =============================================================================
// LOAN DATA
// =============================================================================

export const LOANS: Record<keyof typeof LOAN_IDS, LoanSeedData> = {
  // ============================================================================
  // BRIDGE/FIX & FLIP LOANS
  // ============================================================================

  BRIDGE_MARCUS_AUSTIN_RETAIL: {
    id: LOAN_IDS.BRIDGE_MARCUS_AUSTIN_RETAIL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: 'OMC-2026-0001',
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    propertyId: PROPERTY_IDS.AUSTIN_2ND_STREET_RETAIL,
    loanProduct: SEED_LOAN_PRODUCTS.BRIDGE,
    loanPurpose: 'purchase',
    loanAmount: 2100000,
    purchasePrice: 2800000,
    estimatedValue: 2750000,
    ltvRatio: 0.764,
    interestRate: 0.1025,
    originationFee: 2.0,
    termMonths: 12,
    interestOnlyPeriod: 12,
    creditScore: 745,
    liquidAssets: 850000,
    experienceLevel: 'professional',
    completedDeals: 12,
    status: LOAN_STATUSES.ACTIVE,
    applicationDate: '2025-11-15',
    approvalDate: '2025-11-22',
    fundingDate: '2025-12-05',
    maturityDate: '2026-12-05',
    loanOfficerId: USER_IDS.MIKE_CHEN,
    processorId: USER_IDS.LISA_WANG,
    underwriterId: USER_IDS.SARAH_CHEN,
    notes: 'VIP borrower - 12 successful exits. Value-add retail repositioning.',
  },

  BRIDGE_MARCUS_DALLAS_OFFICE: {
    id: LOAN_IDS.BRIDGE_MARCUS_DALLAS_OFFICE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: 'OMC-2026-0008',
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    propertyId: PROPERTY_IDS.DALLAS_MAIN_OFFICE,
    loanProduct: SEED_LOAN_PRODUCTS.BRIDGE,
    loanPurpose: 'purchase',
    loanAmount: 3150000,
    purchasePrice: 4200000,
    estimatedValue: 4100000,
    ltvRatio: 0.768,
    interestRate: 0.105,
    originationFee: 1.75,
    termMonths: 18,
    interestOnlyPeriod: 18,
    creditScore: 745,
    liquidAssets: 850000,
    experienceLevel: 'professional',
    completedDeals: 12,
    status: LOAN_STATUSES.UNDERWRITING,
    applicationDate: '2026-01-20',
    loanOfficerId: USER_IDS.MIKE_CHEN,
    processorId: USER_IDS.LISA_WANG,
    underwriterId: USER_IDS.SARAH_CHEN,
    notes: 'Portfolio expansion - 5 property package. Priority processing for VIP client.',
  },

  BRIDGE_JESSICA_HOUSTON_SFR: {
    id: LOAN_IDS.BRIDGE_JESSICA_HOUSTON_SFR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: 'OMC-2026-0003',
    consumerId: CONSUMER_IDS.JESSICA_LEE,
    propertyId: PROPERTY_IDS.HOUSTON_HEIGHTS_SFR,
    loanProduct: SEED_LOAN_PRODUCTS.FIX_FLIP,
    loanPurpose: 'purchase',
    loanAmount: 310000,
    purchasePrice: 345000,
    estimatedValue: 335000,
    ltvRatio: 0.925,
    ltcRatio: 0.75,
    interestRate: 0.115,
    originationFee: 2.5,
    termMonths: 9,
    interestOnlyPeriod: 9,
    creditScore: 698,
    liquidAssets: 145000,
    experienceLevel: 'experienced',
    completedDeals: 2,
    status: LOAN_STATUSES.IN_REVIEW,
    applicationDate: '2026-01-18',
    loanOfficerId: USER_IDS.SARAH_WILLIAMS,
    processorId: USER_IDS.LISA_WANG,
    notes: 'Heights bungalow flip. Renovating kitchen/bath. Comp ARV strong.',
  },

  BRIDGE_JENNIFER_AUSTIN_FLIP: {
    id: LOAN_IDS.BRIDGE_JENNIFER_AUSTIN_FLIP,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: 'OMC-2026-0009',
    consumerId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    propertyId: PROPERTY_IDS.AUSTIN_E_7TH_FLIP,
    loanProduct: SEED_LOAN_PRODUCTS.FIX_FLIP,
    loanPurpose: 'purchase',
    loanAmount: 245000,
    purchasePrice: 280000,
    estimatedValue: 270000,
    ltvRatio: 0.907,
    ltcRatio: 0.7,
    interestRate: 0.12,
    originationFee: 3.0,
    termMonths: 9,
    interestOnlyPeriod: 9,
    creditScore: 715,
    liquidAssets: 75000,
    experienceLevel: 'first_time',
    completedDeals: 0,
    status: LOAN_STATUSES.SUBMITTED,
    applicationDate: '2026-01-25',
    notes: 'First-time borrower. Strong credit. Has mentor with 20+ flips.',
  },

  // ============================================================================
  // DSCR RENTAL LOANS
  // ============================================================================

  DSCR_ROBERT_HOUSTON_QUADPLEX: {
    id: LOAN_IDS.DSCR_ROBERT_HOUSTON_QUADPLEX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: 'OMC-2025-0892',
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    propertyId: PROPERTY_IDS.HOUSTON_WESTHEIMER_QUADPLEX,
    loanProduct: SEED_LOAN_PRODUCTS.DSCR,
    loanPurpose: 'refinance',
    loanAmount: 670000,
    estimatedValue: 890000,
    ltvRatio: 0.753,
    interestRate: 0.0825,
    originationFee: 1.5,
    termMonths: 360,
    interestOnlyPeriod: 0,
    creditScore: 768,
    liquidAssets: 1200000,
    experienceLevel: 'professional',
    completedDeals: 15,
    dscrRatio: 1.42,
    monthlyRent: 8200,
    monthlyDebtService: 5774,
    status: LOAN_STATUSES.ACTIVE,
    applicationDate: '2024-09-10',
    approvalDate: '2024-09-18',
    fundingDate: '2024-10-02',
    maturityDate: '2054-10-02',
    loanOfficerId: USER_IDS.DAVID_PARK,
    processorId: USER_IDS.LISA_WANG,
    underwriterId: USER_IDS.SARAH_CHEN,
    notes: 'Cash-out refi to fund next acquisition. Excellent payment history.',
  },

  DSCR_ALICIA_TAMPA_4PLEX: {
    id: LOAN_IDS.DSCR_ALICIA_TAMPA_4PLEX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: 'OMC-2026-0004',
    consumerId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    propertyId: PROPERTY_IDS.TAMPA_BAYSHORE_4PLEX,
    loanProduct: SEED_LOAN_PRODUCTS.DSCR,
    loanPurpose: 'purchase',
    loanAmount: 845000,
    purchasePrice: 1125000,
    estimatedValue: 1115000,
    ltvRatio: 0.758,
    interestRate: 0.0875,
    originationFee: 1.75,
    termMonths: 360,
    creditScore: 728,
    liquidAssets: 450000,
    experienceLevel: 'experienced',
    completedDeals: 7,
    dscrRatio: 1.35,
    monthlyRent: 8800,
    monthlyDebtService: 6519,
    status: LOAN_STATUSES.CONDITIONAL_APPROVAL,
    applicationDate: '2026-01-10',
    approvalDate: '2026-01-22',
    loanOfficerId: USER_IDS.DAVID_PARK,
    processorId: USER_IDS.LISA_WANG,
    underwriterId: USER_IDS.SARAH_CHEN,
    notes: 'Tampa Bay market. All units leased. Strong cash flow.',
  },

  DSCR_DAVID_DALLAS_8UNIT: {
    id: LOAN_IDS.DSCR_DAVID_DALLAS_8UNIT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: 'OMC-2026-0005',
    consumerId: CONSUMER_IDS.DAVID_WILLIAMS,
    propertyId: PROPERTY_IDS.DALLAS_ELM_8UNIT,
    loanProduct: SEED_LOAN_PRODUCTS.DSCR,
    loanPurpose: 'purchase',
    loanAmount: 1237500,
    purchasePrice: 1650000,
    estimatedValue: 1600000,
    ltvRatio: 0.773,
    interestRate: 0.0925,
    originationFee: 1.5,
    termMonths: 360,
    creditScore: 712,
    liquidAssets: 320000,
    experienceLevel: 'experienced',
    completedDeals: 5,
    dscrRatio: 1.28,
    monthlyRent: 14400,
    monthlyDebtService: 11250,
    status: LOAN_STATUSES.IN_REVIEW,
    applicationDate: '2026-01-15',
    loanOfficerId: USER_IDS.SARAH_WILLIAMS,
    processorId: USER_IDS.LISA_WANG,
    notes: 'Value-add 8-unit. Plan to renovate 2 units to increase rents.',
  },

  DSCR_MICHAEL_HOUSTON_TRIPLEX: {
    id: LOAN_IDS.DSCR_MICHAEL_HOUSTON_TRIPLEX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: 'OMC-2026-0006',
    consumerId: CONSUMER_IDS.MICHAEL_BROWN,
    propertyId: PROPERTY_IDS.HOUSTON_MONTROSE_TRIPLEX,
    loanProduct: SEED_LOAN_PRODUCTS.DSCR,
    loanPurpose: 'refinance',
    loanAmount: 543750,
    estimatedValue: 725000,
    ltvRatio: 0.75,
    interestRate: 0.085,
    originationFee: 1.25,
    termMonths: 360,
    creditScore: 705,
    liquidAssets: 180000,
    experienceLevel: 'experienced',
    completedDeals: 3,
    dscrRatio: 1.32,
    monthlyRent: 6450,
    monthlyDebtService: 4886,
    status: LOAN_STATUSES.APPROVED,
    applicationDate: '2026-01-12',
    approvalDate: '2026-01-28',
    loanOfficerId: USER_IDS.DAVID_PARK,
    processorId: USER_IDS.LISA_WANG,
    underwriterId: USER_IDS.SARAH_CHEN,
    notes: 'Refi from hard money at 12%. Strong Montrose location.',
  },

  // ============================================================================
  // CONSTRUCTION LOANS
  // ============================================================================

  CONSTRUCTION_MARIA_SA_MULTIFAMILY: {
    id: LOAN_IDS.CONSTRUCTION_MARIA_SA_MULTIFAMILY,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: '1SC-2026-0001',
    consumerId: CONSUMER_IDS.MARIA_SANTOS,
    propertyId: PROPERTY_IDS.SA_BROADWAY_MULTIFAMILY,
    loanProduct: SEED_LOAN_PRODUCTS.GROUND_UP,
    loanPurpose: 'construction',
    loanAmount: 1800000,
    purchasePrice: 450000,
    estimatedValue: 2400000,
    ltvRatio: 0.75,
    ltcRatio: 0.8,
    interestRate: 0.12,
    originationFee: 2.0,
    termMonths: 18,
    interestOnlyPeriod: 18,
    creditScore: 742,
    liquidAssets: 380000,
    experienceLevel: 'experienced',
    completedDeals: 6,
    constructionBudget: 1800000,
    hardCosts: 1440000,
    softCosts: 180000,
    contingency: 180000,
    drawsRemaining: 5,
    status: LOAN_STATUSES.ACTIVE,
    applicationDate: '2025-10-01',
    approvalDate: '2025-10-20',
    fundingDate: '2025-11-15',
    maturityDate: '2027-05-15',
    loanOfficerId: USER_IDS.SHARPE_ADMIN,
    notes: 'Ground-up 8-unit multifamily. Draw 1 funded. Foundation complete.',
  },

  CONSTRUCTION_CHRIS_FORTWORTH_SFR: {
    id: LOAN_IDS.CONSTRUCTION_CHRIS_FORTWORTH_SFR,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: '1SC-2026-0002',
    consumerId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    propertyId: PROPERTY_IDS.FORTWORTH_CAMP_BOWIE,
    loanProduct: SEED_LOAN_PRODUCTS.FIX_FLIP,
    loanPurpose: 'purchase',
    loanAmount: 370000,
    purchasePrice: 320000,
    estimatedValue: 310000,
    ltvRatio: 0.76,
    ltcRatio: 0.82,
    interestRate: 0.115,
    originationFee: 2.5,
    termMonths: 12,
    interestOnlyPeriod: 12,
    creditScore: 695,
    liquidAssets: 220000,
    experienceLevel: 'experienced',
    completedDeals: 4,
    constructionBudget: 135000,
    hardCosts: 115000,
    softCosts: 10000,
    contingency: 10000,
    drawsRemaining: 1,
    status: LOAN_STATUSES.ACTIVE,
    applicationDate: '2025-09-15',
    approvalDate: '2025-09-28',
    fundingDate: '2025-10-10',
    maturityDate: '2026-10-10',
    loanOfficerId: USER_IDS.SHARPE_ADMIN,
    notes: 'Major renovation - kitchen/bath/HVAC. Final draw pending.',
  },

  // ============================================================================
  // COMMERCIAL LOANS
  // ============================================================================

  COMMERCIAL_ROBERT_HOUSTON_RETAIL: {
    id: LOAN_IDS.COMMERCIAL_ROBERT_HOUSTON_RETAIL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: 'OMC-2025-0645',
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    propertyId: PROPERTY_IDS.HOUSTON_KIRBY_RETAIL,
    loanProduct: SEED_LOAN_PRODUCTS.COMMERCIAL,
    loanPurpose: 'purchase',
    loanAmount: 3600000,
    purchasePrice: 4800000,
    estimatedValue: 4750000,
    ltvRatio: 0.758,
    interestRate: 0.0775,
    originationFee: 1.0,
    termMonths: 120,
    interestOnlyPeriod: 24,
    creditScore: 768,
    liquidAssets: 1200000,
    experienceLevel: 'professional',
    completedDeals: 15,
    dscrRatio: 1.48,
    monthlyRent: 42000,
    monthlyDebtService: 28378,
    status: LOAN_STATUSES.ACTIVE,
    applicationDate: '2025-03-01',
    approvalDate: '2025-03-15',
    fundingDate: '2025-04-01',
    maturityDate: '2035-04-01',
    loanOfficerId: USER_IDS.DAVID_PARK,
    processorId: USER_IDS.LISA_WANG,
    underwriterId: USER_IDS.SARAH_CHEN,
    notes: 'Strip retail with national tenants. NNN leases. Strong DSCR.',
  },

  COMMERCIAL_MARCUS_AUSTIN_OFFICE: {
    id: LOAN_IDS.COMMERCIAL_MARCUS_AUSTIN_OFFICE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    loanNumber: 'OMC-2025-0712',
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    propertyId: PROPERTY_IDS.AUSTIN_CONGRESS_OFFICE,
    loanProduct: SEED_LOAN_PRODUCTS.COMMERCIAL,
    loanPurpose: 'refinance',
    loanAmount: 4125000,
    estimatedValue: 5500000,
    ltvRatio: 0.75,
    interestRate: 0.0725,
    originationFee: 0.75,
    termMonths: 120,
    interestOnlyPeriod: 36,
    creditScore: 745,
    liquidAssets: 850000,
    experienceLevel: 'professional',
    completedDeals: 12,
    dscrRatio: 1.52,
    monthlyRent: 48000,
    monthlyDebtService: 31579,
    status: LOAN_STATUSES.ACTIVE,
    applicationDate: '2025-06-01',
    approvalDate: '2025-06-12',
    fundingDate: '2025-06-28',
    maturityDate: '2035-06-28',
    loanOfficerId: USER_IDS.MIKE_CHEN,
    processorId: USER_IDS.LISA_WANG,
    underwriterId: USER_IDS.SARAH_CHEN,
    notes: 'Congress Ave Class A office. 100% occupied. Tech tenant.',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllLoans(): LoanSeedData[] {
  return Object.values(LOANS);
}

export function getAllProperties(): PropertySeedData[] {
  return Object.values(PROPERTIES);
}

export function getLoansByStatus(status: LoanStatus): LoanSeedData[] {
  return Object.values(LOANS).filter((l) => l.status === status);
}

export function getLoansByProduct(product: SeedLoanProduct): LoanSeedData[] {
  return Object.values(LOANS).filter((l) => l.loanProduct === product);
}

export function getLoansByConsumer(consumerId: string): LoanSeedData[] {
  return Object.values(LOANS).filter((l) => l.consumerId === consumerId);
}

export function getLoansByBusiness(businessId: string): LoanSeedData[] {
  return Object.values(LOANS).filter((l) => l.businessId === businessId);
}

export function getActiveLoans(): LoanSeedData[] {
  return Object.values(LOANS).filter((l) => l.status === LOAN_STATUSES.ACTIVE || l.status === LOAN_STATUSES.FUNDED);
}

export function getPipelineLoans(): LoanSeedData[] {
  const pipelineStatuses: LoanStatus[] = [
    LOAN_STATUSES.SUBMITTED,
    LOAN_STATUSES.IN_REVIEW,
    LOAN_STATUSES.UNDERWRITING,
    LOAN_STATUSES.CONDITIONAL_APPROVAL,
    LOAN_STATUSES.APPROVED,
    LOAN_STATUSES.CLEAR_TO_CLOSE,
  ];
  return Object.values(LOANS).filter((l) => pipelineStatuses.includes(l.status));
}

export function getPropertyById(propertyId: string): PropertySeedData | undefined {
  return Object.values(PROPERTIES).find((p) => p.id === propertyId);
}

export function getPropertiesByType(propertyType: SeedPropertyType): PropertySeedData[] {
  return Object.values(PROPERTIES).filter((p) => p.propertyType === propertyType);
}

// =============================================================================
// E2E LOAN SEED IDs
// =============================================================================

export const E2E_LOAN_SEED_IDS = {
  // Active loans for testing loan operations
  LOAN_ACTIVE_BRIDGE: LOAN_IDS.BRIDGE_MARCUS_AUSTIN_RETAIL,
  LOAN_ACTIVE_DSCR: LOAN_IDS.DSCR_ROBERT_HOUSTON_QUADPLEX,
  LOAN_ACTIVE_CONSTRUCTION: LOAN_IDS.CONSTRUCTION_MARIA_SA_MULTIFAMILY,
  LOAN_ACTIVE_COMMERCIAL: LOAN_IDS.COMMERCIAL_ROBERT_HOUSTON_RETAIL,

  // Pipeline loans for testing workflow
  LOAN_IN_REVIEW: LOAN_IDS.BRIDGE_JESSICA_HOUSTON_SFR,
  LOAN_SUBMITTED: LOAN_IDS.BRIDGE_JENNIFER_AUSTIN_FLIP,
  LOAN_APPROVED: LOAN_IDS.DSCR_MICHAEL_HOUSTON_TRIPLEX,
  LOAN_CONDITIONAL: LOAN_IDS.DSCR_ALICIA_TAMPA_4PLEX,

  // Properties for testing
  PROPERTY_RESIDENTIAL: PROPERTY_IDS.AUSTIN_MANOR_RD_SFR,
  PROPERTY_MULTIFAMILY: PROPERTY_IDS.HOUSTON_WESTHEIMER_QUADPLEX,
  PROPERTY_COMMERCIAL: PROPERTY_IDS.HOUSTON_KIRBY_RETAIL,
  PROPERTY_CONSTRUCTION: PROPERTY_IDS.SA_BROADWAY_MULTIFAMILY,
} as const;

export type E2ELoanSeedId = (typeof E2E_LOAN_SEED_IDS)[keyof typeof E2E_LOAN_SEED_IDS];
